import { useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import { 
  Eye, RotateCcw, FileDown, Loader2, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get today's date in YYYY-MM-DD format
const getTodayISO = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

const initialFormState = {
  // Agreement Details
  agreement_date: getTodayISO(),
  company_legal_name: '',
  cin_llpin: '',
  company_address: '',
  // Billing Details
  invoice_same_as_company: true,
  invoice_address: '',
  invoice_delivery_same_as_invoice: true,
  invoice_delivery_address: '',
  contact_person: '',
  gst_registration_number: '',
  pan_number: '',
  state: '',
  // Commercial Terms
  consultation_fee_type: 'percentage',
  gst_mode: 'gst',
  consultation_fee_value: 50,
  payment_collection_days: 7,
  candidate_leaves_days: 45,
  company_termination_days: 45,
  // Authorized Signatory
  signatory_name: '',
  signatory_designation: ''
};

export default function AgreementGeneratorTab() {
  const [form, setForm] = useState(initialFormState);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    // Clear preview when form changes
    setPreviewHtml(null);
    setPreviewError(null);
  };

  const handlePreview = useCallback(async () => {
    setLoading(true);
    setPreviewError(null);
    
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/agreement-generator/v2/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setPreviewHtml(data.html);
        toast.success('Preview generated');
      } else {
        setPreviewError(data.detail || 'Failed to generate preview');
      }
    } catch (e) {
      setPreviewError('Failed to generate preview');
    }
    
    setLoading(false);
  }, [form]);

  const handleGeneratePdf = useCallback(async () => {
    setGenerating(true);
    
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/agreement-generator/v2/generate-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('PDF generated successfully!');
        // Download the PDF
        const downloadUrl = `${API_URL}/api/agreement-generator/v2/download/${data.id}`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', data.filename);
        // Add auth header via fetch and blob
        const pdfRes = await fetch(downloadUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!pdfRes.ok) {
          let why = '';
          try { why = (await pdfRes.json()).detail || ''; } catch (e) { /* not JSON */ }
          toast.error(why || ('Could not download the agreement (' + pdfRes.status +
                              '). Nothing was saved to your computer.'));
          return;
        }
        const blob = await pdfRes.blob();
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error(data.detail || 'Failed to generate PDF');
      }
    } catch (e) {
      toast.error('Failed to generate PDF');
    }
    
    setGenerating(false);
  }, [form]);

  const handleClear = () => {
    setForm(initialFormState);
    setPreviewHtml(null);
    setPreviewError(null);
    toast.success('Form cleared');
  };

  // Check if required fields are filled for preview
  const canPreview = form.company_legal_name && 
                     form.cin_llpin && 
                     form.company_address && 
                     form.contact_person && 
                     form.gst_registration_number && 
                     form.pan_number && 
                     form.state && 
                     form.signatory_name && 
                     form.signatory_designation;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left Panel - Form */}
      <div className="space-y-6">
        {/* Agreement Details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-emerald-700">Agreement Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Agreement date</Label>
              <Input
                type="date"
                value={form.agreement_date}
                onChange={(e) => updateField('agreement_date', e.target.value)}
                data-testid="agreement-date-input"
              />
            </div>
            <div>
              <Label>Company legal name <span className="text-red-500">*</span></Label>
              <Input
                value={form.company_legal_name}
                onChange={(e) => updateField('company_legal_name', e.target.value)}
                placeholder="ABC Technologies Pvt. Ltd."
                data-testid="company-legal-name-input"
              />
            </div>
            <div>
              <Label>CIN / LLPIN <span className="text-red-500">*</span></Label>
              <Input
                value={form.cin_llpin}
                onChange={(e) => updateField('cin_llpin', e.target.value)}
                placeholder="U72900MH2020PTC123456"
                data-testid="cin-llpin-input"
              />
            </div>
            <div>
              <Label>Company address <span className="text-red-500">*</span></Label>
              <Textarea
                value={form.company_address}
                onChange={(e) => updateField('company_address', e.target.value)}
                placeholder="123 Business Park, Andheri East, Mumbai 400093"
                rows={3}
                data-testid="company-address-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Billing Details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-emerald-700">Billing Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="invoice-same"
                checked={form.invoice_same_as_company}
                onCheckedChange={(checked) => updateField('invoice_same_as_company', checked)}
                data-testid="invoice-same-checkbox"
              />
              <Label htmlFor="invoice-same" className="text-sm font-normal cursor-pointer">
                Invoice address same as company address
              </Label>
            </div>
            
            {!form.invoice_same_as_company && (
              <div>
                <Label>Invoice address</Label>
                <Textarea
                  value={form.invoice_address}
                  onChange={(e) => updateField('invoice_address', e.target.value)}
                  placeholder="Enter invoice address"
                  rows={2}
                  data-testid="invoice-address-input"
                />
              </div>
            )}
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="delivery-same"
                checked={form.invoice_delivery_same_as_invoice}
                onCheckedChange={(checked) => updateField('invoice_delivery_same_as_invoice', checked)}
                data-testid="delivery-same-checkbox"
              />
              <Label htmlFor="delivery-same" className="text-sm font-normal cursor-pointer">
                Invoice delivery address same as invoice address
              </Label>
            </div>
            
            {!form.invoice_delivery_same_as_invoice && (
              <div>
                <Label>Invoice delivery address</Label>
                <Textarea
                  value={form.invoice_delivery_address}
                  onChange={(e) => updateField('invoice_delivery_address', e.target.value)}
                  placeholder="Enter delivery address"
                  rows={2}
                  data-testid="delivery-address-input"
                />
              </div>
            )}
            
            <div>
              <Label>Contact person <span className="text-red-500">*</span></Label>
              <Input
                value={form.contact_person}
                onChange={(e) => updateField('contact_person', e.target.value)}
                placeholder="John Doe"
                data-testid="contact-person-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>GST registration number <span className="text-red-500">*</span></Label>
                <Input
                  value={form.gst_registration_number}
                  onChange={(e) => updateField('gst_registration_number', e.target.value)}
                  placeholder="27AABCU9603R1ZM"
                  data-testid="gst-input"
                />
              </div>
              <div>
                <Label>PAN number <span className="text-red-500">*</span></Label>
                <Input
                  value={form.pan_number}
                  onChange={(e) => updateField('pan_number', e.target.value)}
                  placeholder="AABCU9603R"
                  data-testid="pan-input"
                />
              </div>
            </div>
            
            <div>
              <Label>State <span className="text-red-500">*</span></Label>
              <Input
                value={form.state}
                onChange={(e) => updateField('state', e.target.value)}
                placeholder="Maharashtra"
                data-testid="state-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Commercial Terms */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-emerald-700">Commercial Terms</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Consultation fee type</Label>
                <Select
                  value={form.consultation_fee_type}
                  onValueChange={(value) => updateField('consultation_fee_type', value)}
                >
                  <SelectTrigger data-testid="fee-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed price</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>GST mode</Label>
                <Select
                  value={form.gst_mode}
                  onValueChange={(value) => updateField('gst_mode', value)}
                >
                  <SelectTrigger data-testid="gst-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gst">GST</SelectItem>
                    <SelectItem value="non_gst">NON GST</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Consultation fee value</Label>
                <Input
                  type="number"
                  value={form.consultation_fee_value}
                  onChange={(e) => updateField('consultation_fee_value', parseFloat(e.target.value) || 0)}
                  data-testid="fee-value-input"
                />
              </div>
              <div>
                <Label>Payment collection days</Label>
                <Input
                  type="number"
                  value={form.payment_collection_days}
                  onChange={(e) => updateField('payment_collection_days', parseInt(e.target.value) || 7)}
                  data-testid="payment-days-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Candidate leaves days</Label>
                <Input
                  type="number"
                  value={form.candidate_leaves_days}
                  onChange={(e) => updateField('candidate_leaves_days', parseInt(e.target.value) || 45)}
                  data-testid="candidate-days-input"
                />
              </div>
              <div>
                <Label>Company termination days</Label>
                <Input
                  type="number"
                  value={form.company_termination_days}
                  onChange={(e) => updateField('company_termination_days', parseInt(e.target.value) || 45)}
                  data-testid="termination-days-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Authorized Signatory */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-emerald-700">Authorized Signatory</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Signatory name <span className="text-red-500">*</span></Label>
              <Input
                value={form.signatory_name}
                onChange={(e) => updateField('signatory_name', e.target.value)}
                placeholder="Full name of authorized signatory"
                data-testid="signatory-name-input"
              />
            </div>
            <div>
              <Label>Signatory designation <span className="text-red-500">*</span></Label>
              <Input
                value={form.signatory_designation}
                onChange={(e) => updateField('signatory_designation', e.target.value)}
                placeholder="e.g., Director, CEO, HR Head"
                data-testid="signatory-designation-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handlePreview}
            disabled={loading || !canPreview}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="preview-agreement-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Eye className="w-4 h-4 mr-2" />
            )}
            Preview Agreement
          </Button>
          
          <Button
            variant="outline"
            onClick={handleClear}
            data-testid="clear-form-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Clear
          </Button>
          
          <Button
            onClick={handleGeneratePdf}
            disabled={generating || !previewHtml}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="generate-pdf-btn"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4 mr-2" />
            )}
            Generate PDF
          </Button>
        </div>
        
        {previewHtml && (
          <p className="text-sm text-emerald-600">
            Preview ready. Generate PDF will download the agreement as a PDF.
          </p>
        )}
        
        {!canPreview && (
          <p className="text-sm text-amber-600 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Complete the required fields (marked with *) to preview
          </p>
        )}
      </div>

      {/* Right Panel - Preview */}
      <div className="lg:sticky lg:top-6 h-fit">
        <Card className="min-h-[600px]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Agreement Preview</CardTitle>
          </CardHeader>
          <CardContent>
            {previewError ? (
              <div className="flex flex-col items-center justify-center h-[500px] text-red-500">
                <AlertCircle className="w-12 h-12 mb-4" />
                <p className="text-center">{previewError}</p>
              </div>
            ) : previewHtml ? (
              <div className="border rounded-lg overflow-hidden bg-white">
                <iframe
                  srcDoc={previewHtml}
                  title="Agreement Preview"
                  className="w-full h-[700px]"
                  style={{ border: 'none' }}
                  data-testid="agreement-preview-iframe"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[500px] text-slate-400">
                <Eye className="w-12 h-12 mb-4 opacity-50" />
                <p className="text-center">
                  Complete the required details and click<br />
                  <strong>Preview Agreement</strong>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
